import {
  compose,
  groupBy,
  isEmpty,
  map,
  partition,
  prop,
  sort,
  sortBy,
  sortWith,
} from "ramda";
import { Maybe } from "true-myth";

import {
  IPerson,
  IPosUser,
  IPeople,
  IUnavailabilityBlock,
} from "types/schedules";

import { dateParamToDate, dateToDateParam, sixDaysLater } from "helpers/dates";
import {
  getRoleLabel,
  getRoleCategoryPrecedence,
  getShiftRoleCategoryPrecedence,
} from "helpers/schedule/roles";

function sortByLastName(personA: IPerson, personB: IPerson) {
  const requiredAssignees = [personA.assignee, personB.assignee];
  return Maybe.all(...requiredAssignees).match({
    Just: ([assigneeA, assigneeB]: IPosUser[]) => {
      return assigneeA.lastName.localeCompare(assigneeB.lastName);
    },
    Nothing: () => -1,
  });
}

function sortByFirstName(personA: IPerson, personB: IPerson) {
  const requiredAssignees = [personA.assignee, personB.assignee];
  return Maybe.all(...requiredAssignees).match({
    Just: ([assigneeA, assigneeB]: IPosUser[]) => {
      return assigneeA.firstName.localeCompare(assigneeB.firstName);
    },
    Nothing: () => -1,
  });
}

function sortAlphaByName(people: IPerson[]): IPerson[] {
  return sortWith([sortByLastName, sortByFirstName], people);
}

function sortAlphaByRole(people: IPerson[]): IPerson[] {
  return sort((personA: IPerson, personB: IPerson) => {
    const requiredAssignees = [personA.assignee, personB.assignee];
    return Maybe.all(...requiredAssignees).match({
      Just: ([assigneeA, assigneeB]: IPosUser[]) => {
        return getRoleLabel(assigneeA).localeCompare(getRoleLabel(assigneeB));
      },
      Nothing: () => 1,
    });
  }, people);
}

function sortMultiRoleToTop(people: IPerson[]): IPerson[] {
  return sortBy((person: IPerson) => {
    return person.assignee.match({
      Just: (assignee: IPosUser) =>
        getRoleLabel(assignee) === "Multiple" ? 0 : 1,
      Nothing: () => Infinity,
    });
  }, people);
}

function sortByCategoryPrecedence(people: IPerson[]): IPerson[] {
  return sortBy((person: IPerson) => {
    return person.assignee.match({
      Just: (assignee: IPosUser) => {
        return getRoleCategoryPrecedence(assignee);
      },
      Nothing: () => -1,
    });
  }, people);
}

function sortByCategory(people: IPerson[]): IPerson[] {
  // Sort at three levels:
  // - By category: FoH, BoH, Misc/Other
  //   - For employees with Multiple role, sort into highest precedence category
  // - Alpha by role name
  // - Within single role, alpha by employee last name
  return compose(
    sortByCategoryPrecedence,
    sortMultiRoleToTop,
    sortAlphaByRole,
    sortAlphaByName
  )(people);
}

function sortPeopleShiftsByCategory(people: IPerson[]): IPerson[] {
  return map(
    (person) => ({
      ...person,
      shifts: sortBy(getShiftRoleCategoryPrecedence, person.shifts),
    }),
    people
  );
}

function getOrderedPeople(
  includeUnassigned: boolean,
  people: IPeople
): IPerson[] {
  const [unassigned, assigned]: IPerson[][] = partition((person: IPerson) => {
    return Maybe.isNothing(person.assignee);
  }, people);

  const sortedAssignees: IPerson[] = sortByCategory(assigned);

  if (includeUnassigned) {
    if (unassigned === undefined || unassigned.length === 0) {
      const emptyPerson: IPerson = {
        assignee: Maybe.nothing<IPosUser>(),
        shifts: [],
        unavail: Maybe.nothing(),
      };
      return [emptyPerson, ...sortedAssignees];
    }
    return [...unassigned, ...sortedAssignees];
  } else {
    return sortedAssignees;
  }
}

interface IPublicContainerParams {
  permalink: Maybe<string>;
  startDate: Maybe<Date>;
  endDate: Maybe<Date>;
}

function decodePublicContainerURL(urlInput: string): IPublicContainerParams {
  try {
    const decodedPermalinkAndDate = atob(urlInput);
    const [permalink, startDateParam, endDateParam]: Array<Maybe<string>> =
      decodedPermalinkAndDate.split(" ").map(Maybe.of);
    const startDate = dateParamToDate(startDateParam);
    return {
      permalink,
      startDate,
      endDate: dateParamToDate(endDateParam).or(startDate.map(sixDaysLater)),
    };
  } catch (error) {
    return {
      permalink: Maybe.nothing<string>(),
      startDate: Maybe.nothing<Date>(),
      endDate: Maybe.nothing<Date>(),
    };
  }
}

function encodePublicContainerUrl(params: IPublicContainerParams): string {
  const toJoin = [];
  const permalink = params.permalink.unwrapOr("");
  if (!isEmpty(permalink)) {
    toJoin.push(permalink);
  }
  dateToDateParam(params.startDate).map((startDateParam) =>
    toJoin.push(startDateParam)
  );
  if (toJoin.length === 2) {
    dateToDateParam(params.endDate).map((endDateParam) =>
      toJoin.push(endDateParam)
    );
    return btoa(toJoin.join(" "));
  } else {
    return ""; // error state - both permalink and startDate are required
  }
}

function mergeUnavails(
  people: IPeople,
  unavails: IUnavailabilityBlock[]
): IPeople {
  const unavailsByUser = groupBy<IUnavailabilityBlock>(
    prop("userId"),
    unavails
  );
  return map((person: IPerson) => {
    return person.assignee.match({
      Just: (posUser) => {
        if (unavailsByUser[posUser.uuid]) {
          return {
            ...person,
            unavail: Maybe.of(unavailsByUser[posUser.uuid]),
          };
        } else {
          return person;
        }
      },
      Nothing: () => person,
    });
  }, people);
}

export {
  decodePublicContainerURL,
  encodePublicContainerUrl,
  getOrderedPeople,
  mergeUnavails,
  sortByCategory,
  sortPeopleShiftsByCategory,
};
